/**
 * Client-side permission checks.
 *
 * The server is the enforcement point — every gated route also checks the
 * permission in `backend/app/dependencies.py:require_permission`. What happens
 * here is presentation only: don't show someone a door that will 403.
 *
 * Permission STRINGS are mirrored from `backend/app/permissions.py`, but the
 * role -> permission MAPPING deliberately is not. The server sends each user's
 * effective permissions on `/me`; re-deriving them from `role` in TypeScript
 * would give us two copies of an access-control rule, and the copy that drifts
 * open grants access nobody approved.
 */
import type { User } from '@/contexts/AuthContextBase';

export const PERMISSIONS = {
  DASHBOARD_VIEW: 'dashboard:view',
  REVENUE_VIEW: 'revenue:view',
  OCCUPANCY_VIEW: 'occupancy:view',
  COMPETITORS_VIEW: 'competitors:view',
  RATES_VIEW: 'rates:view',
  RATES_RECOMMEND: 'rates:recommend',
  RATES_APPROVE: 'rates:approve',
  RATES_PUSH: 'rates:push',
  RATES_OVERRIDE: 'rates:override',
  FORECAST_VIEW: 'forecast:view',
  FORECAST_EDIT: 'forecast:edit',
  EVENTS_VIEW: 'events:view',
  EVENTS_CREATE: 'events:create',
  EVENTS_EDIT: 'events:edit',
  MARKETING_VIEW: 'marketing:view',
  FINANCE_VIEW: 'finance:view',
  USERS_VIEW: 'users:view',
  USERS_MANAGE: 'users:manage',
  PROPERTIES_MANAGE: 'properties:manage',
  AUDIT_VIEW: 'audit:view',
  SYSTEM_ADMIN: 'system:admin',
} as const;

export type PermissionKey = keyof typeof PERMISSIONS;
export type PermissionValue = (typeof PERMISSIONS)[PermissionKey];

/** Every permission string. Used by the local dev bypass user only. */
export const ALL_PERMISSIONS: PermissionValue[] = Object.values(PERMISSIONS);

/**
 * Whether the user holds a permission.
 *
 * Default-deny: a null user, or one whose `/me` response carried no
 * permissions, holds nothing. A missing list is treated as an empty list on
 * purpose — an unreachable or half-migrated server should hide navigation,
 * never reveal it.
 */
export function hasPermission(
  user: Pick<User, 'permissions'> | null | undefined,
  permission: PermissionValue,
): boolean {
  if (!user || !Array.isArray(user.permissions)) return false;
  return user.permissions.includes(permission);
}

export function hasAnyPermission(
  user: Pick<User, 'permissions'> | null | undefined,
  permissions: PermissionValue[],
): boolean {
  return permissions.some((permission) => hasPermission(user, permission));
}
